import { Pencil, Plus, Trash } from "lucide-react";
import {
  InfiniteListBase,
  ShowBase,
  useDeleteWithUndoController,
  useRecordContext,
  useShowContext,
  useTranslate,
  type ShowBaseProps,
} from "ra-core";
import { useState } from "react";
import { Link } from "react-router";
import { ReferenceField } from "@/components/admin/reference-field";
import { TextField } from "@/components/admin/text-field";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useIsMobile } from "@/hooks/use-mobile";

import { MobileContent } from "../layout/MobileContent";
import MobileHeader from "../layout/MobileHeader";
import { MobileBackButton } from "../misc/MobileBackButton";
import { NoteCreateSheet } from "../notes/NoteCreateSheet";
import { NotesIterator } from "../notes/NotesIterator";
import { NotesIteratorMobile } from "../notes/NotesIteratorMobile";
import type { Contact } from "../types";
import { Avatar } from "./Avatar";
import { ContactAside } from "./ContactAside";
import { contactFullName } from "./contactModel";

/** Round delete action of the mobile contact page; undoable, like the desktop one. */
const ContactDeleteIconButton = () => {
  const translate = useTranslate();
  const record = useRecordContext<Contact>();
  const { isPending, handleDelete } = useDeleteWithUndoController({
    record,
    resource: "contacts",
    redirect: "list",
  });

  if (!record) return null;

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="rounded-full text-destructive"
      aria-label={translate("ra.action.delete")}
      disabled={isPending}
      onClick={handleDelete}
    >
      <Trash className="size-5" />
    </Button>
  );
};

export const ContactShow = (props: ShowBaseProps = {}) => {
  const isMobile = useIsMobile();

  return (
    <ShowBase
      queryOptions={{
        onError: isMobile
          ? () => {
              /* the mobile content handles the offline case itself */
            }
          : undefined,
      }}
      {...props}
    >
      {isMobile ? <ContactShowMobile /> : <ContactShowDesktop />}
    </ShowBase>
  );
};

const ContactHeadline = () => {
  const { record } = useShowContext<Contact>();
  if (!record) return null;
  return (
    <div className="flex items-center gap-3">
      <Avatar />
      <div className="min-w-0">
        <h2 className="text-xl font-semibold truncate">
          {contactFullName(record)}
        </h2>
        <div className="text-sm text-muted-foreground">
          <ReferenceField source="company_id" reference="companies" link="show">
            <TextField source="name" />
          </ReferenceField>
        </div>
      </div>
    </div>
  );
};

const ContactShowDesktop = () => {
  const { record, isPending } = useShowContext<Contact>();
  if (isPending || !record) return null;

  return (
    <div className="mt-2 mb-2 flex gap-8">
      <div className="flex-1 min-w-0">
        <Card>
          <CardContent className="flex flex-col gap-4">
            <ContactHeadline />
            <Separator />
            <InfiniteListBase
              resource="contact_notes"
              filter={{ contact_id: record.id }}
              sort={{ field: "date", order: "DESC" }}
              perPage={25}
              disableSyncWithLocation
              storeKey={false}
            >
              <NotesIterator reference="contacts" />
            </InfiniteListBase>
          </CardContent>
        </Card>
      </div>
      <ContactAside />
    </div>
  );
};

const ContactShowMobile = () => {
  const translate = useTranslate();
  const { record, isPending } = useShowContext<Contact>();
  const [noteCreateOpen, setNoteCreateOpen] = useState(false);
  if (isPending || !record) return null;

  return (
    <>
      <NoteCreateSheet
        open={noteCreateOpen}
        onOpenChange={setNoteCreateOpen}
        contact_id={record.id}
      />
      <MobileHeader>
        <MobileBackButton />
        <div className="flex flex-1 min-w-0">
          <Link to="/contacts" className="flex-1 min-w-0">
            <h1 className="truncate text-xl font-semibold text-brand-navy">
              {translate("resources.contacts.name", { smart_count: 2 })}
            </h1>
          </Link>
        </div>
        <div className="flex shrink-0 flex-row items-center gap-1">
          <Button
            asChild
            type="button"
            variant="ghost"
            size="icon"
            className="rounded-full"
          >
            <Link
              to={`/contacts/${record.id}`}
              aria-label={translate("resources.contacts.action.edit")}
            >
              <Pencil className="size-5" />
            </Link>
          </Button>
          <ContactDeleteIconButton />
        </div>
      </MobileHeader>
      <MobileContent>
        <div className="mb-4 flex flex-col gap-2">
          <ContactHeadline />
          {record.email ? (
            <a
              className="text-sm underline hover:no-underline break-all"
              href={`mailto:${record.email}`}
            >
              {record.email}
            </a>
          ) : null}
        </div>

        <div className="flex flex-row items-center justify-between gap-2">
          <h3 className="text-lg font-semibold">
            {translate("resources.notes.name", { smart_count: 2 })}
          </h3>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setNoteCreateOpen(true)}
          >
            <Plus className="size-4" />
            {translate("resources.notes.action.add")}
          </Button>
        </div>
        <Separator className="mb-2" />
        <InfiniteListBase
          resource="contact_notes"
          filter={{ contact_id: record.id }}
          sort={{ field: "date", order: "DESC" }}
          perPage={25}
          disableSyncWithLocation
          storeKey={false}
          empty={
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <p className="text-muted-foreground mb-4">
                {translate("resources.notes.empty")}
              </p>
              <Button variant="outline" onClick={() => setNoteCreateOpen(true)}>
                {translate("resources.notes.action.add")}
              </Button>
            </div>
          }
          loading={false}
          error={false}
          queryOptions={{
            onError: () => {
              /* NotesIteratorMobile renders the error itself */
            },
          }}
        >
          <NotesIteratorMobile contactId={record.id} />
        </InfiniteListBase>
      </MobileContent>
    </>
  );
};
